import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || '';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.posthog.com';
const POSTHOG_PROXY_HOST = import.meta.env.VITE_POSTHOG_PROXY_HOST || '';
const POSTHOG_ENABLED = import.meta.env.VITE_POSTHOG_ENABLED === 'true' && !!POSTHOG_KEY;

// Use a same-domain proxy when available. This avoids client-side blocking
// of the PostHog domain by DNS filters, browser privacy features, or adblockers.
const API_HOST = POSTHOG_PROXY_HOST || POSTHOG_HOST;

let initialized = false;

// Captura de exceções: window.onerror + unhandledrejection → PostHog
// ($exception), para termos visibilidade de crashes dos clientes durante os
// eventos. Com throttle simples (10/min) para não inundar se um erro entrar
// em loop.
let exceptionCaptureInstalled = false;
let exceptionBudget = 10;
let exceptionBudgetReset = 0;

// Erros gerados por extensões de browser no contexto da página (ex.:
// carteiras crypto que injectam window.ethereum) — não são acionáveis pela
// app e poluem o feed de exceções do PostHog.
const IGNORED_ERROR_PATTERNS = [
  /window\.ethereum/i,
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-web-extension/i,
];

function isIgnoredError(err: unknown, message?: string, filename?: string): boolean {
  const text = `${message ?? ''} ${filename ?? ''} ${err instanceof Error ? err.message + ' ' + (err.stack ?? '') : String(err ?? '')}`;
  return IGNORED_ERROR_PATTERNS.some((p) => p.test(text));
}

function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  try {
    return new Error(typeof err === 'string' ? err : JSON.stringify(err));
  } catch {
    return new Error(String(err));
  }
}

export function trackException(err: unknown, context?: string) {
  if (!POSTHOG_ENABLED || !initialized) return;
  if (typeof posthog.captureException !== 'function') return;
  const now = Date.now();
  if (now > exceptionBudgetReset) {
    exceptionBudget = 10;
    exceptionBudgetReset = now + 60_000;
  }
  if (exceptionBudget <= 0) return;
  try {
    exceptionBudget--;
    posthog.captureException(toError(err), context ? { source: context } : undefined);
  } catch {
    // Telemetria nunca deve partir a app.
  }
}

function installExceptionCapture() {
  if (exceptionCaptureInstalled || typeof window === 'undefined') return;
  exceptionCaptureInstalled = true;
  window.addEventListener('error', (event) => {
    if (isIgnoredError(event.error, event.message, event.filename)) return;
    trackException(event.error ?? event.message, 'window.onerror');
  });
  window.addEventListener('unhandledrejection', (event) => {
    if (isIgnoredError(event.reason)) return;
    trackException(event.reason, 'unhandledrejection');
  });
}

function sanitizeProperties(properties: Record<string, unknown> = {}) {
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    // Strip only clearly sensitive application fields. Do NOT strip 'token'
    // or 'api_key' — PostHog uses properties.token internally to authenticate
    // event requests, and removing it causes "non-engage request missing event
    // name attribute" errors in PostHog's Rust capture service.
    const lower = key.toLowerCase();
    if (
      lower.includes('password') ||
      lower === 'authtoken' ||
      lower === 'refreshtoken' ||
      lower === 'accesstoken' ||
      lower.includes('secret')
    ) {
      continue;
    }
    cleaned[key] = value;
  }
  return cleaned;
}

export function initPostHog() {
  if (!POSTHOG_ENABLED || initialized) return;

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: API_HOST,
      ui_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: true,
      // Grava 10% das sessões durante os eventos para poder rever o que um
      // user viu quando reporta um problema. Inputs mascarados por defeito.
      session_recording: { sampleRate: 0.1, maskAllInputs: true },
      disable_surveys: true,
      // Avoid Decide fetching through the proxy; we manage feature flags locally/hybrid.
      advanced_disable_decide: true,
      // Disable gzip compression so the proxy receives plain JSON and forwards it intact.
      disable_compression: true,
      sanitize_properties: (props) => sanitizeProperties(props as Record<string, unknown>),
      loaded: (ph) => {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log('[PostHog] loaded', ph.version);
        }
      },
    });
    initialized = true;
    installExceptionCapture();
  } catch (err) {
    // eslint-disable-next-line no-console
    if (import.meta.env.DEV) console.error('[PostHog] init failed', err);
  }
}

export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  if (!POSTHOG_ENABLED) return;
  try {
    posthog.identify(userId, sanitizeProperties(traits ?? {}));
  } catch (err) {
    if (import.meta.env.DEV) console.error('[PostHog] identify failed', err);
  }
}

export function resetUser() {
  if (!POSTHOG_ENABLED) return;
  try {
    posthog.reset();
  } catch (err) {
    if (import.meta.env.DEV) console.error('[PostHog] reset failed', err);
  }
}

export function trackEvent(event: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_ENABLED) return;
  try {
    posthog.capture(event, sanitizeProperties(properties ?? {}));
  } catch (err) {
    if (import.meta.env.DEV) console.error('[PostHog] capture failed', err);
  }
}

export function capturePageView(path?: string) {
  if (!POSTHOG_ENABLED) return;
  try {
    posthog.capture('$pageview', path ? { $current_url: path } : undefined);
  } catch (err) {
    if (import.meta.env.DEV) console.error('[PostHog] pageview failed', err);
  }
}

export function getFeatureFlagVariant(flag: string): string | undefined {
  if (!POSTHOG_ENABLED) return undefined;
  try {
    return posthog.getFeatureFlag(flag) as string | undefined;
  } catch (err) {
    if (import.meta.env.DEV) console.error('[PostHog] getFeatureFlag failed', err);
    return undefined;
  }
}

export function isPostHogEnabled(): boolean {
  return POSTHOG_ENABLED;
}

export { posthog };
